/**
 * AzureConnectionWizard - Modal multi-etapes pour ajouter une connection Azure
 * a un client.
 *
 * Monte dans TenantsPage (Organisation) via un bouton "Connecter Azure".
 *
 * 4 etapes :
 *   1. Pre-requis      : explique les actions a faire cote client (App Reg,
 *                        Reader role) + bouton pour copier le script PowerShell
 *                        d initialisation
 *   2. Saisie creds    : tenant ID, subscription ID, client ID, client secret
 *   3. Test            : dry-run vers ARM pour valider les credentials
 *   4. Sauvegarde      : create + redirection vers la page NIS Azure
 *
 * Securite : le secret reste cote front jusqu au save. Transmis HTTPS au backend
 * qui le stocke en Key Vault et ne le retourne JAMAIS.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent,
  Button, Input, Field, Body1, Caption1, Title3, Spinner,
  MessageBar, MessageBarBody, MessageBarTitle,
  tokens, Divider,
} from '@fluentui/react-components';
import {
  CloudArrowUp24Regular, Key24Regular, CheckmarkCircle24Filled,
  ArrowRight24Regular, ArrowLeft24Regular, Dismiss24Regular,
  Copy24Regular, ArrowDownload24Regular,
} from '@fluentui/react-icons';
import { azureConnectionsApi, type AzureConnectionInput, type AzureConnectionTestResult } from '../api/azureConnectionsApi';

interface AzureConnectionWizardProps {
  open:        boolean;
  onClose:     () => void;
  /** Callback apres creation reussie : on lui passe l id de la connection. */
  onCreated?:  (connectionId: string) => void;
}

// Script PowerShell que le client va executer dans son tenant Azure pour
// creer l App Registration + lui assigner Reader sur la subscription cible.
// On le rend copiable directement dans le wizard.
function buildSetupScript(subscriptionId: string): string {
  // Bloc de resolution de la subscription :
  //  - si l ID est deja saisi dans le wizard -> on le code en dur ;
  //  - sinon -> le script liste les subscriptions et prend l ACTIVE via
  //    `az account show`. On n injecte JAMAIS de placeholder <...> : les
  //    chevrons sont interpretes par cmd.exe (az.cmd) et cassent la commande
  //    ("La syntaxe de la commande n est pas correcte").
  const subBlock = subscriptionId
    ? `\$subscriptionId = '${subscriptionId}'`
    : `# Aucun subscription ID saisi : on liste vos subscriptions et on prend l ACTIVE.
# Pour en cibler une autre, lancez d abord :  az account set --subscription "<nom ou id>"
az account list --query "[].{Nom:name, Id:id, Active:isDefault}" -o table
\$subscriptionId = az account show --query id -o tsv`;

  return `# =============================================================================
#  NIS365 - Setup Azure App Registration + Reader role
#  A executer dans Azure CLI (Cloud Shell ou 'az login' local)
# =============================================================================

\$ErrorActionPreference = 'Stop'

# 0. S assure d etre connecte
if (-not (az account show 2>\$null)) { az login | Out-Null }

# 1. Determine la subscription cible
${subBlock}
if (-not \$subscriptionId) {
    Write-Error "Subscription ID introuvable. Lancez 'az login', puis 'az account set --subscription <id>', et relancez ce script."
    exit 1
}
Write-Host "Subscription cible : \$subscriptionId" -ForegroundColor Cyan

# 2. Cree l App Registration
\$appName = 'nis365-reader-' + (Get-Random -Minimum 1000 -Maximum 9999)
Write-Host "Creating App Registration : \$appName"
\$app = az ad app create --display-name \$appName --query '{appId:appId,id:id}' | ConvertFrom-Json

# 3. Cree le Service Principal lie
\$sp = az ad sp create --id \$app.appId --query '{id:id}' | ConvertFrom-Json

# 4. Genere un secret valide 1 an
\$secret = az ad app credential reset --id \$app.appId --years 1 --query '{password:password}' | ConvertFrom-Json

# 5. Laisse Entra ID propager le nouveau Service Principal (evite un echec de role assignment)
Start-Sleep -Seconds 15

# 6. Assigne le role Reader sur la subscription cible.
#    Commandes sur UNE seule ligne (pas de continuation backtick, source de
#    "syntaxe incorrecte" si un espace traine apres le \`).
az role assignment create --assignee \$app.appId --role 'Reader' --scope "/subscriptions/\$subscriptionId" | Out-Null

# 7. (Recommande) Security Reader pour Defender for Cloud
az role assignment create --assignee \$app.appId --role 'Security Reader' --scope "/subscriptions/\$subscriptionId" | Out-Null

# Affiche les valeurs a saisir dans NIS365
Write-Host "" -ForegroundColor Green
Write-Host "==================== A COPIER DANS NIS365 ====================" -ForegroundColor Green
Write-Host "Tenant ID       : \$(az account show --query tenantId -o tsv)"
Write-Host "Subscription ID : \$subscriptionId"
Write-Host "Client ID       : \$(\$app.appId)"
Write-Host "Client Secret   : \$(\$secret.password)"
Write-Host "=============================================================" -ForegroundColor Green
Write-Host "Le secret est valide 1 an. Pensez a le faire tourner."
`;
}

export function AzureConnectionWizard({ open, onClose, onCreated }: AzureConnectionWizardProps) {
  const { t } = useTranslation();

  const [step, setStep]           = useState(0);
  const [error, setError]         = useState<string | null>(null);

  // Step 2 : credentials
  const [name, setName]                     = useState('');
  const [tenantId, setTenantId]             = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [clientId, setClientId]             = useState('');
  const [clientSecret, setClientSecret]     = useState('');

  // Step 3 : test result
  const [testing, setTesting]               = useState(false);
  const [testResult, setTestResult]         = useState<AzureConnectionTestResult | null>(null);

  // Step 4 : save
  const [saving, setSaving]                 = useState(false);

  // -------------------- Actions --------------------
  function resetWizard() {
    setStep(0);
    setError(null);
    setName('');
    setTenantId('');
    setSubscriptionId('');
    setClientId('');
    setClientSecret('');
    setTestResult(null);
  }

  function close() {
    resetWizard();
    onClose();
  }

  async function copyScript() {
    try {
      await navigator.clipboard.writeText(buildSetupScript(subscriptionId));
    } catch { /* fallthrough silent */ }
  }

  async function downloadScript() {
    const blob = new Blob([buildSetupScript(subscriptionId)], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nis365-setup-azure.ps1';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function runTest() {
    setError(null);
    setTesting(true);
    setTestResult(null);
    try {
      const input: AzureConnectionInput = { name, tenantId, subscriptionId, appClientId: clientId, clientSecret };
      const r = await azureConnectionsApi.testDryRun(input);
      setTestResult(r);
      if (!r.success) {
        setError(r.error ?? t('azureWizard.test.errorGeneric'));
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setTesting(false);
    }
  }

  async function save() {
    setError(null);
    setSaving(true);
    try {
      const input: AzureConnectionInput = { name, tenantId, subscriptionId, appClientId: clientId, clientSecret };
      const created = await azureConnectionsApi.create(input);
      onCreated?.(created.id);
      close();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // -------------------- Validation --------------------
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const step2Valid =
    !!name.trim() &&
    guidRegex.test(tenantId.trim()) &&
    guidRegex.test(subscriptionId.trim()) &&
    guidRegex.test(clientId.trim()) &&
    clientSecret.trim().length >= 8;
  const step3Valid = testResult?.success === true;

  // ============================================================================
  //                                  RENDER
  // ============================================================================
  if (!open) return null;
  const stepLabels = [
    t('azureWizard.step1.title'),
    t('azureWizard.step2.title'),
    t('azureWizard.step3.title'),
    t('azureWizard.step4.title'),
  ];

  return (
    <Dialog
      open={open}
      modalType="non-modal"
      onOpenChange={(_, data) => { if (!data.open) close(); }}
    >
      <DialogSurface style={{ maxWidth: 760, width: '95vw' }}>
        <DialogBody>
          <DialogTitle>
            {t('azureWizard.title')}
            <Caption1 style={{ display: 'block', marginTop: 4, color: tokens.colorNeutralForeground3 }}>
              {t('azureWizard.subtitle')}
            </Caption1>
          </DialogTitle>

          {/* Stepper */}
          <Stepper current={step} labels={stepLabels} />

          <DialogContent style={{ minHeight: 380 }}>
            {error && (
              <MessageBar intent="error" style={{ marginBottom: 12 }}>
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}

            {step === 0 && (
              <Step1Prerequisites
                subscriptionId={subscriptionId}
                onCopyScript={copyScript}
                onDownloadScript={downloadScript}
              />
            )}

            {step === 1 && (
              <Step2Credentials
                name={name} setName={setName}
                tenantId={tenantId} setTenantId={setTenantId}
                subscriptionId={subscriptionId} setSubscriptionId={setSubscriptionId}
                clientId={clientId} setClientId={setClientId}
                clientSecret={clientSecret} setClientSecret={setClientSecret}
              />
            )}

            {step === 2 && (
              <Step3Test
                testing={testing}
                testResult={testResult}
                onRunTest={runTest}
              />
            )}

            {step === 3 && (
              <Step4Save
                name={name}
                subscriptionName={testResult?.subscriptionName}
                tenantName={testResult?.tenantName}
                roles={testResult?.rolesGranted}
              />
            )}
          </DialogContent>

          {/* Footer */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginTop: 16, paddingTop: 16,
            borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
          }}>
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={close}
            >
              {t('azureWizard.cancel')}
            </Button>
            <div style={{ flex: 1 }} />
            {step > 0 && (
              <Button
                appearance="secondary"
                icon={<ArrowLeft24Regular />}
                onClick={() => setStep(step - 1)}
                disabled={saving || testing}
              >
                {t('azureWizard.back')}
              </Button>
            )}

            {step === 0 && (
              <Button
                appearance="primary"
                icon={<ArrowRight24Regular />}
                iconPosition="after"
                onClick={() => setStep(1)}
              >
                {t('azureWizard.next')}
              </Button>
            )}

            {step === 1 && (
              <Button
                appearance="primary"
                icon={<ArrowRight24Regular />}
                iconPosition="after"
                onClick={() => { setTestResult(null); setStep(2); }}
                disabled={!step2Valid}
              >
                {t('azureWizard.next')}
              </Button>
            )}

            {step === 2 && (
              <Button
                appearance="primary"
                icon={<ArrowRight24Regular />}
                iconPosition="after"
                onClick={() => setStep(3)}
                disabled={!step3Valid}
              >
                {t('azureWizard.next')}
              </Button>
            )}

            {step === 3 && (
              <Button
                appearance="primary"
                icon={<CheckmarkCircle24Filled />}
                iconPosition="after"
                onClick={save}
                disabled={saving || !step3Valid}
              >
                {saving ? <Spinner size="tiny" /> : t('azureWizard.save')}
              </Button>
            )}
          </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ============================================================================
//                              SUB-COMPONENTS
// ============================================================================
function Stepper({ current, labels }: { current: number; labels: string[] }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${labels.length}, 1fr)`,
      gap: 6,
      margin: '16px 0 4px',
    }}>
      {labels.map((label, i) => {
        const done   = i < current;
        const active = i === current;
        const bg = done ? tokens.colorPaletteGreenBackground3
                : active ? tokens.colorBrandBackground
                : tokens.colorNeutralBackground3;
        const fg = done || active ? '#fff' : tokens.colorNeutralForeground3;
        return (
          <div key={i} style={{
            padding: '6px 8px', borderRadius: 6,
            background: bg, color: fg,
            fontSize: '0.75rem', fontWeight: 600,
            textAlign: 'center',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {i + 1}. {label}
          </div>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Step 1 : Pre-requis + script
// ----------------------------------------------------------------------------
function Step1Prerequisites({ subscriptionId, onCopyScript, onDownloadScript }: {
  subscriptionId: string;
  onCopyScript: () => void;
  onDownloadScript: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <CloudArrowUp24Regular style={{ color: tokens.colorBrandForeground1, width: 32, height: 32 }} />
        <div>
          <Title3 style={{ margin: 0 }}>{t('azureWizard.step1.heading')}</Title3>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('azureWizard.step1.intro')}</Caption1>
        </div>
      </div>
      <Body1>{t('azureWizard.step1.body')}</Body1>
      <ul style={{ margin: 0, paddingLeft: 20, color: tokens.colorNeutralForeground2 }}>
        <li>{t('azureWizard.step1.req1')}</li>
        <li>{t('azureWizard.step1.req2')}</li>
        <li>{t('azureWizard.step1.req3')}</li>
      </ul>
      <Divider />
      <Body1 style={{ fontWeight: 600 }}>{t('azureWizard.step1.scriptTitle')}</Body1>
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
        {t('azureWizard.step1.scriptHint')}
      </Caption1>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button appearance="primary" icon={<Copy24Regular />} onClick={onCopyScript}>
          {t('azureWizard.step1.scriptCopy')}
        </Button>
        <Button appearance="secondary" icon={<ArrowDownload24Regular />} onClick={onDownloadScript}>
          {t('azureWizard.step1.scriptDownload')}
        </Button>
      </div>
      {!subscriptionId && (
        <Caption1 style={{ color: tokens.colorPaletteYellowForeground1 }}>
          {t('azureWizard.step1.scriptPlaceholderWarn')}
        </Caption1>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Step 2 : Saisie credentials
// ----------------------------------------------------------------------------
function Step2Credentials(props: {
  name: string;          setName: (s: string) => void;
  tenantId: string;      setTenantId: (s: string) => void;
  subscriptionId: string; setSubscriptionId: (s: string) => void;
  clientId: string;      setClientId: (s: string) => void;
  clientSecret: string;  setClientSecret: (s: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Body1>{t('azureWizard.step2.body')}</Body1>
      <Field label={t('azureWizard.step2.nameLabel')} hint={t('azureWizard.step2.nameHint')} required>
        <Input value={props.name} onChange={(_, d) => props.setName(d.value)} placeholder="Prod West Europe" />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label={t('azureWizard.step2.tenantIdLabel')} required>
          <Input value={props.tenantId} onChange={(_, d) => props.setTenantId(d.value)} placeholder="00000000-0000-0000-0000-000000000000" />
        </Field>
        <Field label={t('azureWizard.step2.subscriptionIdLabel')} required>
          <Input value={props.subscriptionId} onChange={(_, d) => props.setSubscriptionId(d.value)} placeholder="00000000-0000-0000-0000-000000000000" />
        </Field>
        <Field label={t('azureWizard.step2.clientIdLabel')} required>
          <Input value={props.clientId} onChange={(_, d) => props.setClientId(d.value)} placeholder="00000000-0000-0000-0000-000000000000" />
        </Field>
        <Field label={t('azureWizard.step2.clientSecretLabel')} required hint={t('azureWizard.step2.clientSecretHint')}>
          <Input type="password" value={props.clientSecret} onChange={(_, d) => props.setClientSecret(d.value)} />
        </Field>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Step 3 : Test connection
// ----------------------------------------------------------------------------
function Step3Test({ testing, testResult, onRunTest }: {
  testing: boolean;
  testResult: AzureConnectionTestResult | null;
  onRunTest: () => void;
}) {
  const { t } = useTranslation();
  if (testing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px', gap: 12 }}>
        <Spinner label={t('azureWizard.step3.testing')} />
      </div>
    );
  }
  if (testResult?.success) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px', gap: 12 }}>
        <CheckmarkCircle24Filled style={{ width: 56, height: 56, color: tokens.colorPaletteGreenForeground1 }} />
        <Title3>{t('azureWizard.step3.successTitle')}</Title3>
        <Body1 style={{ textAlign: 'center', color: tokens.colorNeutralForeground2 }}>
          {t('azureWizard.step3.successBody', {
            sub: testResult.subscriptionName ?? '—',
            tenant: testResult.tenantName ?? '—',
          })}
        </Body1>
        {testResult.rolesGranted && testResult.rolesGranted.length > 0 && (
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            {t('azureWizard.step3.rolesGranted', { roles: testResult.rolesGranted.join(', ') })}
          </Caption1>
        )}
      </div>
    );
  }
  if (testResult && !testResult.success) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>{t('azureWizard.step3.errorTitle')}</MessageBarTitle>
            {testResult.error}
          </MessageBarBody>
        </MessageBar>
        {testResult.hint && (
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{testResult.hint}</Caption1>
        )}
        <Button appearance="primary" onClick={onRunTest}>{t('azureWizard.step3.retry')}</Button>
      </div>
    );
  }
  // pas encore teste
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Body1>{t('azureWizard.step3.intro')}</Body1>
      <ul style={{ margin: 0, paddingLeft: 20, color: tokens.colorNeutralForeground2 }}>
        <li>{t('azureWizard.step3.check1')}</li>
        <li>{t('azureWizard.step3.check2')}</li>
        <li>{t('azureWizard.step3.check3')}</li>
      </ul>
      <Button
        appearance="primary"
        icon={<Key24Regular />}
        onClick={onRunTest}
        style={{ alignSelf: 'flex-start' }}
      >
        {t('azureWizard.step3.runButton')}
      </Button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Step 4 : Save / Summary
// ----------------------------------------------------------------------------
function Step4Save({ name, subscriptionName, tenantName, roles }: {
  name: string;
  subscriptionName?: string;
  tenantName?: string;
  roles?: string[];
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '20px 0' }}>
      <Title3>{t('azureWizard.step4.heading')}</Title3>
      <Body1>{t('azureWizard.step4.body')}</Body1>
      <div style={{
        padding: 16,
        background: tokens.colorNeutralBackground2,
        borderRadius: 8,
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        rowGap: 8,
        columnGap: 16,
      }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('azureWizard.step4.nameField')}</Caption1>
        <Body1 style={{ fontWeight: 600 }}>{name}</Body1>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('azureWizard.step4.tenantField')}</Caption1>
        <Body1>{tenantName ?? '—'}</Body1>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('azureWizard.step4.subField')}</Caption1>
        <Body1>{subscriptionName ?? '—'}</Body1>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('azureWizard.step4.rolesField')}</Caption1>
        <Body1>{roles?.join(', ') ?? '—'}</Body1>
      </div>
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
        {t('azureWizard.step4.secureNote')}
      </Caption1>
    </div>
  );
}
